import { request } from '@/api/request';
import { encrypt } from '@/utils/crypto';
import { ProductId } from '../enum/account';

/**
 * Create account
 * @param params Account creation parameters
 * @returns Promise
 */
export function createAccount(params: Account.CreateParams): Promise<any> {
  return request({
    url: '/rs-enterprise/api/v1/account',
    method: 'POST',
    data: params
  });
}

/**
 * Get account by ID
 * @param id Account ID
 * @returns Promise
 */
export function getAccountById(id: number): Promise<any> {
  return request({
    url: `/rs-enterprise/api/v1/account/${id}`,
    method: 'GET'
  });
}

/**
 * Update account
 * @param id Account ID
 * @param params Account update parameters
 * @returns Promise
 */
export function updateAccount(id: number, params: Account.UpdateParams): Promise<any> {
  return request({
    url: `/rs-enterprise/api/v1/account/${id}`,
    method: 'PUT',
    data: params
  });
}

/**
 * Delete account
 * @param id Account ID
 * @returns Promise
 */
export function deleteAccount(id: number): Promise<any> {
  return request({
    url: `/id-enterprise/api/account-info/${id}`,
    method: 'DELETE'
  });
}

/**
 * Request email change
 * @param params New email parameters
 * @returns Promise
 */
export function requestEmailChange(params: Account.EmailChangeParams): Promise<any> {
  return request({
    url: '/rs-enterprise/api/v1/account/email-change/request',
    method: 'POST',
    data: params
  });
}

/**
 * Get account list
 * @param params Account list query parameters
 * @returns Promise
 */
export function getAccountList(params: Account.GetListParams): Promise<any> {
  return request({
    url: '/id-enterprise/api/account-info/account-management-list',
    method: 'POST',
    data: params
  });
}
export function changePassword(params: Account.ChangePasswordParams): Promise<any> {
  const p = {
    oldPassword:  encrypt(params.oldPassword),
    newPassword:  encrypt(params.newPassword),
    confirmPassword:  encrypt(params.confirmPassword),
  }
  return request({
    url: '/rs-enterprise/api/v1/account/change-password',
    method: 'POST',
    data: p
  });
}

/**
 * Get email suggestions
 * @param params Email suggestion parameters
 * @returns Promise<Account.EmailSuggestionItem[]>
 */
export function getEmailSuggestions(params: Account.EmailSuggestionParams): Promise<Account.EmailSuggestionItem[]> {
  return request({
    url: '/id-enterprise/api/account-info/email-suggestion',
    method: 'POST',
    data: params
  });
}

/**
 * Check if email exists
 * @param params Check email parameters
 * @returns Promise<boolean>
 */
export function checkEmail(params: Account.CheckEmailParams): Promise<{
		status: number, // 状态码：0-不存在，1-已存在（当前企业），2-已存在于其他企业
		description: string
	}> {
  return request({
    url: '/id-enterprise/api/account-info/check-email',
    method: 'POST',
    data: params
  });
}

/**
 * Create or update account info
 * @param params Account info parameters
 * @returns Promise<Account.AccountInfoResponse>
 */
export function saveAccountInfo(params: Account.AccountInfoParams): Promise<Account.AccountInfoResponse> {
  return request({
    url: '/id-enterprise/api/account-info/save-account',
    method: 'POST',
    data: params
  });
}

/**
 * Get roles list
 * @returns Promise<Account.RoleInfo[]>
 */
export function getRoles(): Promise<Account.productRoles[]> {
  return request({
    url: '/id-enterprise/api/account-info/roles/product',
    method: 'GET'
  });
}

/**
 * Get account detail by ID
 * @param id Account ID
 * @returns Promise<Account.AccountDetailResponse>
 */
export function getAccountDetail(id: number, productId: number): Promise<Account.AccountDetailResponse> {
  return request({
    url: `/id-enterprise/api/account-info/${id}/${productId}`,
    method: 'GET'
  });
}

/**
 * Get user profile detail
 * @returns Promise<Account.ProfileDetailResponse>
 */
export function getProfileDetail(): Promise<Account.ProfileDetailResponse> {
  return request({
    url: '/id-enterprise/api/user/profile/detail',
    method: 'GET',
    noProductId: true
  });
}

/**
 * Update user email
 * @param params Update email parameters
 * @returns Promise<boolean>
 */
export function updateProfileEmail(params: Account.UpdateEmailParams): Promise<boolean> {
  return request({
    url: '/id-enterprise/api/user/profile/email',
    method: 'PUT',
    data: params
  });
}

/**
 * Update user password
 * @param params Update password parameters
 * @returns Promise<any>
 */
export function updateProfilePassword(params: Account.UpdatePasswordParams): Promise<any> {
  const encryptedParams = {
    currentPassword: encrypt(params.currentPassword),
    newPassword: encrypt(params.newPassword),
    confirmPassword: encrypt(params.confirmPassword)
  };
  return request({
    url: '/id-enterprise/api/user/profile/password',
    method: 'PUT',
    data: encryptedParams
  });
}

/**
 * Update username
 * @param params Update username parameters
 * @returns Promise<any>
 */
export function updateProfileUsername(params: Account.UpdateUsernameParams): Promise<any> {
  return request({
    url: '/id-enterprise/api/user/profile/username',
    method: 'PUT',
    data: params
  });
}

